import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { AoSGameState } from '../../types';
import { endGameBarbados } from '../utils/gameEnd';

/**
 * Phase 10: ターン進行フェーズ
 *
 * ターンフラグをリセットし、次のターンに進みます。
 * ロジック処理後、ユーザーがクリックするまで待機します。
 * Barbados: アクションマーカーリセットはPhase 3で実施（全4種使用時のみ）
 * Barbados: 10ターン終了後はゲーム終了
 *
 * 参照:
 * - docs/specifications/logic/common/phase_10_advance_turn.md
 * - docs/specifications/logic/barbados/phase_10_advance_turn_override.md
 */
export const advanceTurnPhase: PhaseConfig<AoSGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G, ctx, events }: { G: AoSGameState; ctx: any; events: any }) => {
    debug.log(`[advanceTurnPhase] ★★★ Phase 10: Advance Turn started ★★★`);
    debug.log(`[advanceTurnPhase] ★★★ ctx:`, { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });

    // ターン番号はPhase 1 (issueShares) でインクリメントされる
    const currentActualTurn = (G as any).actualTurn || 1;
    debug.log(`[advanceTurnPhase] Current actualTurn: ${currentActualTurn}`);

    // ゲーム終了判定（Barbados: 10ターン終了後）
    const maxTurns = 10; // Barbados固定
    if (currentActualTurn >= maxTurns) {
      debug.log(`[advanceTurnPhase] ★★★ Game ending (actualTurn ${currentActualTurn} >= ${maxTurns})`);
      // ゲーム終了処理（株式買い戻し・勝敗判定）
      endGameBarbados(G, ctx, events);
      return; // ゲーム終了なので Phase 遷移しない
    }

    debug.log(`[advanceTurnPhase] Game continues (actualTurn ${currentActualTurn} < ${maxTurns})`);

    // 全プレイヤーのターンフラグをリセット
    for (const playerId of ctx.playOrder) {
      const player = G.players[playerId];

      // ターンフラグリセット
      player.selectedAction = null;
      player.tilesPlacedThisTurn = 0;
      player.newCityTilesPlacedThisTurn = 0;
      player.hasPassedBuild = false;
      player.hasUpgradedLocoThisTurn = false;
      player.goodsPlacedThisTurn = 0;
      player.orderBidAmount = 0;

      // Phase 5 状態リセット（Issue #6修正）
      player.turnsCompletedThisPhase = 0;
      player.goodsTransportedThisPhase = 0;

      // Barbados専用: 今ターンの株式発行数とフラグをリセット
      if ('currentTurnSharesIssued' in player) {
        (player as any).currentTurnSharesIssued = 0;
        (player as any).hasExecutedIssueSharesMove = false;
      }
    }

    // アクションマーカーリセットはselectActionsBarbadosPhase.onBeginで実施（全4種使用時のみ）
    debug.log(`[advanceTurnPhase] Turn flags reset, ready for next turn`);

    // 処理結果を保存（UI表示用）- 次ターン番号を表示
    const nextTurn = currentActualTurn + 1;
    G.autoPhaseResult = {
      phase: 'advanceTurn',
      message: 'Advancing to next turn...',
      floatingText: `Turn ${nextTurn}`,
      details: { newTurn: nextTurn },
    };

    debug.log('[advanceTurnPhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: AoSGameState; events: any }) => {
      // Phase内でのみ呼び出し可能（Boardgame.ioが保証）
      G.autoPhaseResult = null;
      events.endPhase();
    },
  },

  onEnd: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    debug.log('[advanceTurnPhase] ★★★ onEnd called - Phase 10 ended ★★★');
    debug.log('[advanceTurnPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase });
    debug.log(`[advanceTurnPhase] ★★★ actualTurn: ${(G as any).actualTurn}`);
    debug.log('[advanceTurnPhase] ★★★ Next phase should be: issueShares');

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('advanceTurn');
  },

  // 次フェーズへ遷移（Phase 1に戻る）
  next: 'issueShares'
};
